import React, { useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Paper,
  Alert,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TablePagination,
  CircularProgress,
  Backdrop,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import InventoryIcon from '@mui/icons-material/Inventory';
import ArchiveIcon from '@mui/icons-material/Archive';
import PrintIcon from '@mui/icons-material/Print';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ItemCard from './ItemCard';
import ItemListView from './ItemListView';

import InventorySearchBar from './InventorySearchBar';
import InventoryFilters from './InventoryFilters';

const ItemList = ({
  displayedItems,
  startEditing,
  markAsSold,
  markAsMissing,
  deleteItem,
  checkItemTransactions,
  archiveItem,
  quickAddItem,
  hideMessage,
  channelsMap = {},
  searchQuery,
  onSearchChange,
  onAddItem,
  statusFilter,
  onStatusFilterChange,
  selectedChannelId,
  onSelectChannel,
  availableChannels = [],
  totalCount = 0,
  page = 0,
  rowsPerPage = 25,
  onPageChange,
  onRowsPerPageChange,
  isSearching = false,
  isLoading = false,
  showArchiveBrowser = false,
  onToggleArchiveBrowser,
  onPrintLabel,
  labelRequiredChannelIds = new Set(),
  sortField = null,
  sortOrder = 'asc',
  onSortChange,
  onRequestBarcodes,
  isRequestingBarcodes = false,
  quailhqChannelIds = new Set(),
}) => {
  const [viewMode, setViewMode] = useState('list');
  const [selectedItems, setSelectedItems] = useState([]);
  const itemCount = totalCount || displayedItems.length;

  const handleToggleSelect = (itemId) => {
    setSelectedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const eligibleItems = displayedItems.filter(item => item.itemNumber);

  const handleSelectAll = () => {
    const allEligibleIds = eligibleItems.map(item => item.id);
    const allSelected = allEligibleIds.every(id => selectedItems.includes(id));
    if (allSelected) {
      setSelectedItems(prev => prev.filter(id => !allEligibleIds.includes(id)));
    } else {
      setSelectedItems(prev => [...new Set([...prev, ...allEligibleIds])]);
    }
  };

  const handleBulkPrint = () => {
    if (onPrintLabel && selectedItems.length > 0) {
      const itemsToPrint = displayedItems.filter(
        item => selectedItems.includes(item.id) && item.itemNumber && labelRequiredChannelIds.has(item.salesChannelId)
      );
      if (itemsToPrint.length > 0) {
        // Call the bulk print handler from the parent
        onPrintLabel(itemsToPrint, true);
      }
    }
  };
  const isInitialLoading = isLoading && displayedItems.length === 0;
  const showPagination = itemCount > rowsPerPage || page > 0 || isSearching;
  const renderPagination = () => {
    if (!showPagination) return null;
    return (
      <TablePagination
        component="div"
        count={itemCount}
        page={page}
        onPageChange={(event, newPage) => onPageChange?.(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) =>
          onRowsPerPageChange?.(parseInt(event.target.value, 10))
        }
        rowsPerPageOptions={[50]}
      />
    );
  };

  return (
    <Box sx={{ py: 4 }}>
      {/* Search and Filters Bar - reorganized layout */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {/* View Toggle - Left */}
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, newView) => newView && setViewMode(newView)}
          size="small"
        >
          <ToggleButton value="grid" aria-label="grid view">
            <GridViewIcon />
          </ToggleButton>
          <ToggleButton value="list" aria-label="list view">
            <ViewListIcon />
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Search Bar - Center */}
        <InventorySearchBar
          searchQuery={searchQuery}
          onSearchChange={onSearchChange}
        />

        {/* Filters */}
        <InventoryFilters
          statusFilter={statusFilter}
          onStatusFilterChange={onStatusFilterChange}
          selectedChannelId={selectedChannelId}
          onSelectChannel={onSelectChannel}
          availableChannels={availableChannels}
        />

        {/* Archive Toggle */}
        {onToggleArchiveBrowser && (
          <Button
            variant={showArchiveBrowser ? 'contained' : 'outlined'}
            size="small"
            startIcon={<ArchiveIcon />}
            onClick={onToggleArchiveBrowser}
          >
            {showArchiveBrowser ? 'Hide Archive' : 'View Archive'}
          </Button>
        )}

        {/* Pagination - right aligned */}
        {showPagination && (
          <Box sx={{ marginLeft: 'auto' }}>
            {renderPagination()}
          </Box>
        )}
      </Box>

      {/* Bulk Action Bar */}
      {selectedItems.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 2,
            p: 1.5,
            px: 2,
            bgcolor: 'primary.50',
            border: '1px solid',
            borderColor: 'primary.200',
            borderRadius: 1,
          }}
        >
          <Box sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
            {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''} selected
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<PrintIcon />}
            onClick={handleBulkPrint}
            sx={{ textTransform: 'none' }}
            disabled={!displayedItems.some(item => selectedItems.includes(item.id) && item.itemNumber && labelRequiredChannelIds.has(item.salesChannelId))}
          >
            Print Labels
          </Button>
          {onRequestBarcodes && displayedItems.some(item => selectedItems.includes(item.id) && quailhqChannelIds.has(item.salesChannelId)) && (
            <Button
              variant="contained"
              size="small"
              startIcon={isRequestingBarcodes ? <CircularProgress size={16} color="inherit" /> : <QrCode2Icon />}
              onClick={() => {
                const ids = selectedItems.filter(id => {
                  const item = displayedItems.find(i => i.id === id);
                  return item && quailhqChannelIds.has(item.salesChannelId);
                });
                if (ids.length > 500) {
                  alert('Maximum 500 items per barcode request. Please select fewer items.');
                  return;
                }
                onRequestBarcodes(ids);
                setSelectedItems([]);
              }}
              disabled={isRequestingBarcodes}
              sx={{ textTransform: 'none' }}
            >
              Request Barcodes
            </Button>
          )}
          <Button
            variant="text"
            size="small"
            onClick={() => setSelectedItems([])}
            sx={{ textTransform: 'none' }}
          >
            Clear Selection
          </Button>
        </Box>
      )}

      {/* Items Grid/List or Empty State with Loading Overlay */}
      <Box sx={{ position: 'relative', minHeight: 200 }}>
        {/* Loading overlay - shows when loading with existing items */}
        {isLoading && !isInitialLoading && (
          <Backdrop
            open={true}
            sx={{
              position: 'absolute',
              zIndex: (theme) => theme.zIndex.drawer + 1,
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              borderRadius: 2,
            }}
          >
            <CircularProgress />
          </Backdrop>
        )}

        {isInitialLoading ? (
          viewMode === 'grid' ? (
            <Grid container spacing={2}>
              {Array.from({ length: 8 }).map((_, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={`loading-card-${index}`}>
                  <Paper sx={{ p: 2 }}>
                    <Skeleton variant="rectangular" height={140} sx={{ mb: 2 }} />
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="text" width="80%" />
                    <Skeleton variant="text" width="40%" />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          ) : (
            <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
              <Table>
                <TableBody>
                  {Array.from({ length: 6 }).map((_, index) => (
                    <TableRow key={`loading-row-${index}`}>
                      <TableCell><Skeleton variant="rectangular" height={48} width={60} /></TableCell>
                      <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                      <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                      <TableCell><Skeleton variant="text" width="40%" /></TableCell>
                      <TableCell><Skeleton variant="text" width="50%" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : displayedItems.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            padding: 4,
            textAlign: 'center',
            backgroundColor: 'grey.100',
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'grey.400',
          }}
        >
          <InventoryIcon
            sx={{
              fontSize: 48,
              color: 'grey.400',
              mb: 2,
              display: 'block',
            }}
          />
          <Alert severity="info">
            {hideMessage}
          </Alert>
        </Paper>
      ) : viewMode === 'grid' ? (
        <Grid container spacing={2}>
          {displayedItems.map(item => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={item.id} sx={{ display: 'flex' }}>
              <ItemCard
                item={item}
                startEditing={startEditing}
                markAsSold={markAsSold}
                markAsMissing={markAsMissing}
                deleteItem={deleteItem}
                checkItemTransactions={checkItemTransactions}
                archiveItem={archiveItem}
                channelsMap={channelsMap}
                onPrintLabel={onPrintLabel}
                labelRequiredChannelIds={labelRequiredChannelIds}
                onRequestBarcodes={onRequestBarcodes}
                isRequestingBarcodes={isRequestingBarcodes}
                quailhqChannelIds={quailhqChannelIds}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <ItemListView
          displayedItems={displayedItems}
          startEditing={startEditing}
          markAsSold={markAsSold}
          markAsMissing={markAsMissing}
          deleteItem={deleteItem}
          checkItemTransactions={checkItemTransactions}
          archiveItem={archiveItem}
          channelsMap={channelsMap}
          onPrintLabel={onPrintLabel}
          labelRequiredChannelIds={labelRequiredChannelIds}
          onRequestBarcodes={onRequestBarcodes}
          isRequestingBarcodes={isRequestingBarcodes}
          quailhqChannelIds={quailhqChannelIds}
          selectedItems={selectedItems}
          onToggleSelect={handleToggleSelect}
          onSelectAll={handleSelectAll}
          sortField={sortField}
          sortOrder={sortOrder}
          onSortChange={onSortChange}
        />
      )}
      </Box>

      {showPagination && (
        <Box sx={{ mt: 2 }}>
          {renderPagination()}
        </Box>
      )}
    </Box>
  );
};

export default ItemList;
